import type {
  CSSProperties,
  ChangeEvent,
  PointerEvent as ReactPointerEvent,
  ReactNode,
} from "react";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import {
  Button,
  Cascader,
  Input,
  InputNumber,
  Popover,
  Select,
  Tooltip,
} from "antd";
import type { DefaultOptionType } from "antd/es/cascader";
import {
  DisconnectOutlined,
  DownOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import { ColorPicker } from "../../../BaseComps/ColorPicker";
import { ReactComponent as ConnectIcon } from "../../../icons-and-animations/layer-inspector/connect.svg";
import { layerInspectorPopupProps } from "./layerInspectorPopup";
import { ANCHOR_POSITIONS } from "./types";
import type {
  AnchorPosition,
  ApplyFieldValue,
  ApplyLayerFont,
  FieldConnectionActions,
  FreezeAnimatedFields,
  FontSelectOption,
  OpenVisualPropertyConnector,
  VisualEditableField,
} from "./types";
import {
  coerceInputNumber,
  getActiveFieldControls,
  getAnimatedFields,
  getConnectionDisplayInfo,
  FONT_SOURCE_ORDER,
  hasBlockedField,
  isMixedValueField,
  roundToTwoDecimals,
  toColorFieldValue,
  toColorHex,
  toVector,
  updateLinkedScaleComponent,
  updateVectorComponent,
} from "./utils";

const INDEX_LABELS = ["X", "Y", "Z"];
const CONNECT_POPOVER_DELAY_MS = 1000;
const CONNECT_POPOVER_CLOSE_DELAY_MS = 120;
const FieldConnectionActionsContext = createContext<
  FieldConnectionActions | undefined
>(undefined);

type FontFamilyGroup = {
  key: string;
  source: FontSelectOption["source"];
  family: string;
  options: FontSelectOption[];
};

type FontStyleGroup = {
  style: "normal" | "italic";
  label: string;
  options: FontSelectOption[];
};

type FontCascaderOption = DefaultOptionType & {
  value: string;
  label: ReactNode;
  searchText?: string;
  fontOption?: FontSelectOption;
  children?: FontCascaderOption[];
};

type FontPreviewLabelProps = {
  option: FontSelectOption;
  preview?: FontSelectOption["preview"];
  title?: string;
  size?: "family" | "variant";
};

const FONT_PREVIEW_CLASS = "layer-visual-panel__font-cascader-preview";

const isFontSelectOption = (
  option: FontSelectOption | undefined
): option is FontSelectOption => Boolean(option);

const getFontOptionSortValue = (option: FontSelectOption) => {
  const numericWeight = Number(option.weight);
  return Number.isFinite(numericWeight) ? numericWeight : 0;
};

const getFontOptionCascaderValue = (option: FontSelectOption) =>
  `font:${option.source}:${option.value}`;

function getFontOptionCascaderPath(
  option: FontSelectOption,
  familyKey: string
): string[] {
  if (option.source === "animation") {
    return [getFontOptionCascaderValue(option)];
  }

  return [familyKey, getFontOptionCascaderValue(option)];
}

const getFontStyleSortValue = (style: string) => (style === "normal" ? 0 : 1);

const getFontFamilyLabel = (option: FontSelectOption) =>
  option.family || option.fontName || option.label || option.value;

const getFontOptionStyle = (option: FontSelectOption): "normal" | "italic" => {
  if (option.style === "italic") {
    return "italic";
  }

  return option.styleName?.toLowerCase().includes("italic")
    ? "italic"
    : "normal";
};

const FONT_WEIGHT_LABELS: Record<string, string> = {
  "100": "Thin",
  "200": "Extra Light",
  "300": "Light",
  "400": "Normal",
  "500": "Medium",
  "600": "Semi Bold",
  "700": "Bold",
  "800": "Extra Bold",
  "900": "Black",
};

const getFontWeightLabel = (option: FontSelectOption) => {
  if (option.weightName) {
    return option.weightName;
  }

  const styleName = option.styleName
    ?.replace(/italic/gi, "")
    .replace(/\s+/g, " ")
    .trim();

  if (styleName && !/^regular$/i.test(styleName)) {
    return styleName;
  }

  if (option.weight && FONT_WEIGHT_LABELS[option.weight]) {
    return FONT_WEIGHT_LABELS[option.weight];
  }

  return option.style === "italic" ? "Italic" : "Normal";
};

const buildFontFamilyGroups = (
  options: FontSelectOption[]
): FontFamilyGroup[] => {
  const groups = new Map<string, FontFamilyGroup>();

  options.forEach((option) => {
    const family = getFontFamilyLabel(option);
    const key = `${option.source}:${family}`;
    const group = groups.get(key) ?? {
      key,
      source: option.source,
      family,
      options: [],
    };
    group.options.push(option);
    groups.set(key, group);
  });

  return Array.from(groups.values()).sort((left, right) => {
    if (left.source !== right.source) {
      return FONT_SOURCE_ORDER[left.source] - FONT_SOURCE_ORDER[right.source];
    }

    return left.family.localeCompare(right.family);
  });
};

const buildFontStyleGroups = (options: FontSelectOption[]): FontStyleGroup[] =>
  Array.from(
    options.reduce((styles, option) => {
      const style = getFontOptionStyle(option);
      const styleOptions = styles.get(style) ?? [];
      styleOptions.push(option);
      styles.set(style, styleOptions);
      return styles;
    }, new Map<"normal" | "italic", FontSelectOption[]>())
  )
    .sort(
      ([left], [right]) =>
        getFontStyleSortValue(left) - getFontStyleSortValue(right)
    )
    .map(([style, styleOptions]) => ({
      style,
      label: style === "italic" ? "Italic" : "Normal",
      options: styleOptions.sort(
        (left, right) =>
          getFontOptionSortValue(left) - getFontOptionSortValue(right) ||
          getFontWeightLabel(left).localeCompare(getFontWeightLabel(right))
      ),
    }));

const renderFontOptionLabel = (label: ReactNode) => (
  <span className="layer-visual-panel__font-cascader-option">
    <span>{label}</span>
  </span>
);

const getPreviewFontFamily = (option: FontSelectOption) =>
  [option.family, option.fontName]
    .filter(Boolean)
    .map((fontName) => `"${String(fontName).replace(/"/g, '\\"')}"`)
    .join(", ");

const getPreviewFontStyle = (option: FontSelectOption): CSSProperties => ({
  fontFamily: getPreviewFontFamily(option),
  fontStyle: getFontOptionStyle(option),
  fontWeight: option.weight,
});

const getStaticFontPreviewUrl = (preview: FontSelectOption["preview"]) =>
  preview?.mode === "staticSvg" ? preview.svgUrl ?? preview.svgAssetPath : "";

function FontPreviewLabel({
  option,
  preview = option.preview,
  title,
  size = "variant",
}: FontPreviewLabelProps) {
  const [imageFailed, setImageFailed] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const label = title || option.label || getFontFamilyLabel(option);
  const staticPreviewUrl = getStaticFontPreviewUrl(preview);

  useEffect(() => {
    setImageFailed(false);
    setImageLoaded(false);
  }, [staticPreviewUrl]);

  if (staticPreviewUrl && !imageFailed) {
    return (
      <span
        className={[
          FONT_PREVIEW_CLASS,
          `${FONT_PREVIEW_CLASS}--static`,
          `${FONT_PREVIEW_CLASS}--${size}`,
          !imageLoaded ? `${FONT_PREVIEW_CLASS}--loading` : "",
        ]
          .filter(Boolean)
          .join(" ")}
        title={label}
      >
        {!imageLoaded && (
          <span className={`${FONT_PREVIEW_CLASS}-loader`} aria-hidden="true" />
        )}
        <img
          src={staticPreviewUrl}
          alt={label}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageFailed(true)}
        />
      </span>
    );
  }

  if (preview?.mode === "hostRender") {
    return (
      <span
        className={`${FONT_PREVIEW_CLASS} ${FONT_PREVIEW_CLASS}--host`}
        title={label}
        style={getPreviewFontStyle(option)}
      >
        {preview.text || label}
      </span>
    );
  }

  if (option.source === "animation" && (option.family || option.fontName)) {
    return (
      <span
        className={`${FONT_PREVIEW_CLASS} ${FONT_PREVIEW_CLASS}--host`}
        title={label}
        style={getPreviewFontStyle(option)}
      >
        {label}
      </span>
    );
  }

  return <>{label}</>;
}

const getFontPreviewOption = (options: FontSelectOption[]) =>
  options.find(
    (option) =>
      getFontOptionStyle(option) === "normal" && option.weight === "400"
  ) ??
  options.find((option) => getFontOptionStyle(option) === "normal") ??
  options[0];

const renderFontSectionLabel = (label: string) => (
  <span className="layer-visual-panel__font-cascader-section">{label}</span>
);

const buildFontVariantOptions = (
  fontOptions: FontSelectOption[]
): FontCascaderOption[] => {
  const styleGroups = buildFontStyleGroups(fontOptions);
  const showStyleSections = styleGroups.length > 1;

  return styleGroups.flatMap((styleGroup) => [
    ...(showStyleSections
      ? [
          {
            value: `section:${styleGroup.style}`,
            label: renderFontSectionLabel(styleGroup.label),
            disabled: true,
          },
        ]
      : []),
    ...styleGroup.options.map((option) => ({
      value: getFontOptionCascaderValue(option),
      label: renderFontOptionLabel(
        option.preview ? (
          <FontPreviewLabel option={option} />
        ) : (
          getFontWeightLabel(option)
        )
      ),
      searchText: [
        option.family,
        option.styleName,
        option.weightName,
        option.weight,
        option.value,
      ]
        .filter(Boolean)
        .join(" "),
      fontOption: option,
    })),
  ]);
};

const buildFlatFontOption = (option: FontSelectOption): FontCascaderOption => ({
  value: getFontOptionCascaderValue(option),
  label: renderFontOptionLabel(
    <FontPreviewLabel option={option} title={option.label} />
  ),
  searchText: [
    option.family,
    option.label,
    option.styleName,
    option.weightName,
    option.weight,
    option.value,
  ]
    .filter(Boolean)
    .join(" "),
  fontOption: option,
});

const FONT_SECTIONS: Record<
  FontSelectOption["source"],
  { value: string; label: string }
> = {
  animation: { value: "section:animation", label: "Fonts in this scene" },
  account: { value: "section:account", label: "Account fonts" },
  google: { value: "section:external", label: "Other fonts" },
};

export const buildFontCascaderOptions = (
  familyGroups: FontFamilyGroup[]
): FontCascaderOption[] =>
  familyGroups.flatMap((group, index) => {
    const options: FontCascaderOption[] = [];
    const isAnimationGroup = group.source === "animation";
    const groupLabelText =
      isAnimationGroup && group.options.length === 1
        ? group.options[0].label
        : group.family;
    const previewOption = getFontPreviewOption(group.options);
    const groupPreview = previewOption?.familyPreview ?? previewOption?.preview;
    const groupLabel =
      previewOption && groupPreview ? (
        <FontPreviewLabel
          option={previewOption}
          preview={groupPreview}
          size="family"
          title={groupLabelText}
        />
      ) : (
        groupLabelText
      );
    const isFirstGroupOfSource =
      familyGroups[index - 1]?.source !== group.source;

    if (isFirstGroupOfSource) {
      const section = FONT_SECTIONS[group.source];
      options.push({
        value: section.value,
        label: renderFontSectionLabel(section.label),
        disabled: true,
      });
    }

    if (isAnimationGroup) {
      options.push(...group.options.map(buildFlatFontOption));
      return options;
    }

    options.push({
      value: group.key,
      label: groupLabel,
      searchText: `${group.family} ${groupLabelText}`,
      children: buildFontVariantOptions(group.options),
    });

    return options;
  });

export function AnimatedLock({
  fields,
  onFreeze,
}: {
  fields: VisualEditableField[];
  onFreeze: FreezeAnimatedFields;
}) {
  if (!fields.length) {
    return null;
  }

  return (
    <div className="layer-visual-panel__animated-lock">
      <span className="layer-visual-panel__animated-pill">Animated</span>
      <Tooltip title="Disable animation">
        <Button
          size="small"
          aria-label="Disable animation"
          icon={<DisconnectOutlined />}
          onClick={() => onFreeze(fields)}
        />
      </Tooltip>
    </div>
  );
}

function MixedValueTag() {
  return (
    <Tooltip title="Selected layers have different values for this property">
      <span className="layer-visual-panel__mixed-pill">mixed</span>
    </Tooltip>
  );
}

const hasActiveConnection = (
  fields: Array<VisualEditableField | undefined>,
  componentIndex?: number
) => Boolean(getActiveFieldControls(fields, { componentIndex }).length);

export function FieldConnectionSummary({
  fields,
  actions,
  componentIndex,
}: {
  fields: Array<VisualEditableField | undefined>;
  actions?: FieldConnectionActions;
  componentIndex?: number;
}) {
  const contextActions = useContext(FieldConnectionActionsContext);
  const effectiveActions = actions ?? contextActions;
  const controls = getActiveFieldControls(fields, { componentIndex });

  if (!controls.length) {
    return null;
  }

  const [primaryControl, ...extraControls] = controls;
  const display = getConnectionDisplayInfo(primaryControl);
  const modId = effectiveActions?.getModIdByIndex(primaryControl.modIndex);
  const canOpen = Boolean(modId);
  const content = (
    <>
      <span
        className={`layer-visual-panel__connection-tag layer-visual-panel__connection-tag--${display.className}`}
      >
        {display.label}
      </span>
      {display.detail ? (
        <span className="layer-visual-panel__connection-value">
          {display.detail}
        </span>
      ) : null}
      {extraControls.length ? (
        <span className="layer-visual-panel__connection-count">
          +{extraControls.length}
        </span>
      ) : null}
    </>
  );

  if (!canOpen) {
    return (
      <span
        className="layer-visual-panel__connection-summary"
        title={display.title}
      >
        {content}
      </span>
    );
  }

  return (
    <button
      type="button"
      className="layer-visual-panel__connection-summary layer-visual-panel__connection-summary--clickable"
      title={display.title}
      onMouseEnter={() => effectiveActions?.onHoverMod(modId)}
      onMouseLeave={() => effectiveActions?.onHoverMod(undefined)}
      onClick={(event) => {
        if (!modId) return;
        event.stopPropagation();
        effectiveActions?.onOpenMod(modId);
      }}
    >
      {content}
    </button>
  );
}

export function PanelSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="layer-visual-panel__section">
      <div className="layer-visual-panel__section-header">
        <div className="layer-visual-panel__section-title">{title}</div>
      </div>
      <div className="layer-visual-panel__section-body">{children}</div>
    </section>
  );
}

export function CollapsibleRows({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <details className="layer-visual-panel__collapsible">
      <summary className="layer-visual-panel__collapsible-summary">
        <span>{title}</span>
        <DownOutlined />
      </summary>
      <div className="layer-visual-panel__collapsible-body">{children}</div>
    </details>
  );
}

export function ConnectorHoverPopover({
  children,
  onConnect,
}: {
  children: ReactNode;
  onConnect?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const showTimerRef = useRef<number | undefined>();
  const closeTimerRef = useRef<number | undefined>();

  const clearShowTimer = () => {
    if (showTimerRef.current === undefined) return;
    window.clearTimeout(showTimerRef.current);
    showTimerRef.current = undefined;
  };

  const clearCloseTimer = () => {
    if (closeTimerRef.current === undefined) return;
    window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = undefined;
  };

  const scheduleOpen = () => {
    if (!onConnect) return;
    clearCloseTimer();
    if (open) return;
    clearShowTimer();
    showTimerRef.current = window.setTimeout(() => {
      showTimerRef.current = undefined;
      setOpen(true);
    }, CONNECT_POPOVER_DELAY_MS);
  };

  const scheduleClose = () => {
    clearShowTimer();
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => {
      closeTimerRef.current = undefined;
      setOpen(false);
    }, CONNECT_POPOVER_CLOSE_DELAY_MS);
  };

  useEffect(
    () => () => {
      clearShowTimer();
      clearCloseTimer();
    },
    []
  );

  if (!onConnect) {
    return <>{children}</>;
  }

  return (
    <Popover
      open={open}
      trigger={[]}
      placement="left"
      getPopupContainer={layerInspectorPopupProps.getPopupContainer}
      overlayClassName="layer-visual-panel__connect-popover"
      content={
        <div
          data-layer-inspector-keep-selection
          onMouseEnter={clearCloseTimer}
          onMouseLeave={scheduleClose}
          onPointerDown={(event) => event.stopPropagation()}
        >
          <Button
            className="layer-visual-panel__connect-action"
            size="small"
            type="text"
            icon={
              <ConnectIcon className="layer-visual-panel__connect-action-icon" />
            }
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              clearShowTimer();
              clearCloseTimer();
              setOpen(false);
              onConnect();
            }}
          >
            Connect
          </Button>
        </div>
      }
    >
      <div
        className="layer-visual-panel__connect-trigger"
        data-layer-inspector-keep-selection
        onMouseEnter={scheduleOpen}
        onMouseLeave={scheduleClose}
        onFocusCapture={scheduleOpen}
        onBlurCapture={scheduleClose}
      >
        {children}
      </div>
    </Popover>
  );
}

export function PropertyRow({
  label,
  fields,
  children,
  onFreeze,
  compact,
  connectValue = true,
  connectionActions,
  onOpenConnector,
}: {
  label: string;
  fields: Array<VisualEditableField | undefined>;
  children?: ReactNode;
  onFreeze: FreezeAnimatedFields;
  compact?: boolean;
  connectValue?: boolean;
  connectionActions?: FieldConnectionActions;
  onOpenConnector?: OpenVisualPropertyConnector;
}) {
  const animatedFields = getAnimatedFields(fields);
  const isControlled = hasBlockedField(fields);
  const hasMixedValue = fields.some(isMixedValueField);
  const hasValueContent =
    children !== undefined &&
    children !== null &&
    children !== false &&
    children !== "";
  const showChildValues =
    hasValueContent && (!animatedFields.length || isControlled);
  const handleOpenConnector = onOpenConnector
    ? () => onOpenConnector(fields)
    : undefined;
  const renderConnectableValue = (value: ReactNode) => {
    if (
      value === undefined ||
      value === null ||
      value === false ||
      value === "" ||
      !connectValue
    ) {
      return value;
    }

    return (
      <ConnectorHoverPopover onConnect={handleOpenConnector}>
        {value}
      </ConnectorHoverPopover>
    );
  };

  return (
    <FieldConnectionActionsContext.Provider value={connectionActions}>
      <div
        className={`layer-visual-panel__row${
          compact ? " layer-visual-panel__row--compact" : ""
        }${animatedFields.length ? " layer-visual-panel__row--animated" : ""}${
          hasMixedValue ? " layer-visual-panel__row--mixed" : ""
        }`}
      >
        <div className="layer-visual-panel__row-label">{label}</div>
        {hasMixedValue ? (
          <div className="layer-visual-panel__row-control">
            {renderConnectableValue(<MixedValueTag />)}
          </div>
        ) : showChildValues ? (
          <div className="layer-visual-panel__row-control">
            {renderConnectableValue(children)}
          </div>
        ) : isControlled ? (
          <div className="layer-visual-panel__row-control">
            {renderConnectableValue(
              <FieldConnectionSummary
                fields={fields}
                actions={connectionActions}
              />
            )}
          </div>
        ) : animatedFields.length ? null : hasValueContent ? (
          <div className="layer-visual-panel__row-control">
            {renderConnectableValue(children)}
          </div>
        ) : null}
        {!isControlled && !hasMixedValue && (
          <AnimatedLock fields={animatedFields} onFreeze={onFreeze} />
        )}
      </div>
    </FieldConnectionActionsContext.Provider>
  );
}

export function RotationWheelControl({
  field,
  onChange,
}: {
  field: VisualEditableField;
  onChange: ApplyFieldValue;
}) {
  const value =
    typeof field.value === "number" && Number.isFinite(field.value)
      ? roundToTwoDecimals(field.value)
      : 0;
  const visualAngle = ((value % 360) + 360) % 360;

  const updateFromPointer = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const radians = Math.atan2(
      event.clientY - centerY,
      event.clientX - centerX
    );
    const nextValue = Math.round((radians * 180) / Math.PI + 90);
    onChange(field, nextValue < -180 ? nextValue + 360 : nextValue);
  };

  return (
    <div className="layer-visual-panel__rotation-control">
      <button
        type="button"
        className="layer-visual-panel__rotation-wheel"
        style={{ "--rotation-angle": `${visualAngle}deg` } as CSSProperties}
        aria-label="Rotation wheel"
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          updateFromPointer(event);
        }}
        onPointerMove={(event) => {
          if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            updateFromPointer(event);
          }
        }}
      >
        <span />
      </button>
      <NumericControl
        field={field}
        onChange={onChange}
        unit="deg"
        prefix="R"
        compact
      />
    </div>
  );
}

export function NumericControl({
  field,
  onChange,
  unit,
  disabled,
  prefix,
  compact,
  tooltip,
  className,
  onOpenConnector,
}: {
  field: VisualEditableField;
  onChange: ApplyFieldValue;
  unit?: string;
  disabled?: boolean;
  prefix?: string;
  compact?: boolean;
  tooltip?: ReactNode;
  className?: string;
  onOpenConnector?: OpenVisualPropertyConnector;
}) {
  const classNames = `layer-visual-panel__number-field${
    className ? ` ${className}` : ""
  }`;

  if (hasActiveConnection([field])) {
    return (
      <div className={classNames}>
        <FieldConnectionSummary fields={[field]} />
      </div>
    );
  }

  const value =
    typeof field.value === "number" && Number.isFinite(field.value)
      ? roundToTwoDecimals(field.value)
      : undefined;

  const input = (
    <InputNumber
      className={`breakpoint-input layer-visual-panel__number-input${
        compact ? " layer-visual-panel__number-input--compact" : ""
      }`}
      addonBefore={prefix}
      aria-label={typeof tooltip === "string" ? tooltip : field.label}
      value={value}
      disabled={disabled || field.isAnimated}
      formatter={(entry) =>
        unit && entry != null ? `${entry}${unit}` : `${entry}`
      }
      parser={(entry) => {
        const parsed = Number(String(entry ?? "").replace(unit ?? "", ""));
        return Number.isFinite(parsed) ? parsed : 0;
      }}
      onChange={(nextValue) => {
        const normalizedValue = coerceInputNumber(nextValue);
        if (normalizedValue !== undefined) {
          onChange(field, normalizedValue);
        }
      }}
    />
  );

  return (
    <div className={classNames}>
      <ConnectorHoverPopover
        onConnect={onOpenConnector ? () => onOpenConnector([field]) : undefined}
      >
        {tooltip ? <Tooltip title={tooltip}>{input}</Tooltip> : input}
      </ConnectorHoverPopover>
    </div>
  );
}

export function VectorControl({
  field,
  onChange,
  count = 2,
  unit,
  disabled,
  compact,
  linked,
  onOpenConnector,
}: {
  field: VisualEditableField;
  onChange: ApplyFieldValue;
  count?: number;
  unit?: string;
  disabled?: boolean;
  compact?: boolean;
  linked?: boolean;
  onOpenConnector?: OpenVisualPropertyConnector;
}) {
  const values = toVector(field).slice(0, count).map(roundToTwoDecimals);
  const renderConnectableInput = (index: number, input: JSX.Element) => (
    <ConnectorHoverPopover
      onConnect={
        onOpenConnector
          ? () => onOpenConnector([field], { componentIndex: index })
          : undefined
      }
    >
      {input}
    </ConnectorHoverPopover>
  );

  return (
    <div
      className={`layer-visual-panel__split-controls${
        compact ? " layer-visual-panel__split-controls--compact" : ""
      }`}
    >
      {values.map((value, index) => (
        <div
          className="layer-visual-panel__number-field"
          key={`${field.rootPath}-${index}`}
        >
          {hasActiveConnection([field], index) ? (
            <FieldConnectionSummary fields={[field]} componentIndex={index} />
          ) : (
            renderConnectableInput(
              index,
              <InputNumber
                className={`breakpoint-input layer-visual-panel__number-input${
                  compact ? " layer-visual-panel__number-input--compact" : ""
                }`}
                addonBefore={INDEX_LABELS[index] ?? index + 1}
                aria-label={`${field.label} ${
                  INDEX_LABELS[index] ?? index + 1
                }`}
                value={value}
                disabled={disabled || field.isAnimated}
                formatter={(entry) =>
                  unit && entry != null ? `${entry}${unit}` : `${entry}`
                }
                parser={(entry) => {
                  const parsed = Number(
                    String(entry ?? "").replace(unit ?? "", "")
                  );
                  return Number.isFinite(parsed) ? parsed : 0;
                }}
                onChange={(nextEntry) => {
                  const normalizedEntry = coerceInputNumber(nextEntry);
                  if (normalizedEntry !== undefined) {
                    if (linked && index < 2) {
                      updateLinkedScaleComponent(
                        field,
                        index,
                        normalizedEntry,
                        onChange
                      );
                    } else {
                      updateVectorComponent(
                        field,
                        index,
                        normalizedEntry,
                        onChange
                      );
                    }
                  }
                }}
              />
            )
          )}
        </div>
      ))}
    </div>
  );
}

export function TextControl({
  field,
  onChange,
}: {
  field: VisualEditableField;
  onChange: ApplyFieldValue;
}) {
  if (hasActiveConnection([field])) {
    return <FieldConnectionSummary fields={[field]} />;
  }

  const value = typeof field.value === "string" ? field.value : "";

  return (
    <Input.TextArea
      className="layer-visual-panel__text-area"
      value={value}
      autoSize={{ minRows: 2, maxRows: 4 }}
      onChange={(event) => onChange(field, event.target.value)}
    />
  );
}

export function StringControl({
  field,
  onChange,
}: {
  field: VisualEditableField;
  onChange: ApplyFieldValue;
}) {
  if (hasActiveConnection([field])) {
    return <FieldConnectionSummary fields={[field]} />;
  }

  const value = typeof field.value === "string" ? field.value : "";

  return (
    <Input
      value={value}
      onChange={(event) => onChange(field, event.target.value)}
    />
  );
}

export function FontSelectControl({
  field,
  value: valueOverride,
  options,
  onChange,
}: {
  field: VisualEditableField;
  value?: string;
  options: FontSelectOption[];
  onChange: ApplyLayerFont;
}) {
  if (hasActiveConnection([field])) {
    return <FieldConnectionSummary fields={[field]} />;
  }

  const value =
    valueOverride ?? (typeof field.value === "string" ? field.value : "");
  const normalizedOptions = Array.from(
    new Map(
      [
        value
          ? {
              value,
              label: value,
              replacement: value,
              source: "animation" as const,
            }
          : undefined,
        ...options,
      ]
        .filter(isFontSelectOption)
        .map((option) => [getFontOptionCascaderValue(option), option])
    ).values()
  );
  const familyGroups = buildFontFamilyGroups(normalizedOptions);
  const selectedOption =
    normalizedOptions.find(
      (option) => option.value === value && option.source === "animation"
    ) ?? normalizedOptions.find((option) => option.value === value);
  const selectedFamilyKey = selectedOption
    ? `${selectedOption.source}:${getFontFamilyLabel(selectedOption)}`
    : undefined;
  const selectedLabel = selectedOption?.label || value;
  const cascaderOptions = buildFontCascaderOptions(familyGroups);
  const selectedValue =
    selectedOption && selectedFamilyKey
      ? getFontOptionCascaderPath(selectedOption, selectedFamilyKey)
      : undefined;
  const fontOptionsByValue = new Map(
    normalizedOptions.map((option) => [
      getFontOptionCascaderValue(option),
      option,
    ])
  );

  return (
    <Cascader<FontCascaderOption>
      className="layer-visual-panel__font-select"
      popupClassName="layer-visual-panel__font-cascader-popup"
      value={selectedValue}
      options={cascaderOptions}
      disabled={field.isAnimated}
      placeholder="Select font"
      displayRender={() => selectedLabel || ""}
      showSearch={{
        filter: (inputValue, path) =>
          path.some((option) =>
            String(option.searchText ?? option.value)
              .toLowerCase()
              .includes(inputValue.toLowerCase())
          ),
      }}
      getPopupContainer={layerInspectorPopupProps.getPopupContainer}
      popupRender={layerInspectorPopupProps.popupRender}
      onChange={(nextValue) => {
        const nextFontValue = Array.isArray(nextValue)
          ? nextValue[nextValue.length - 1]
          : undefined;

        if (typeof nextFontValue !== "string") {
          return;
        }

        const selectedOption = fontOptionsByValue.get(nextFontValue);
        onChange(selectedOption?.replacement ?? nextFontValue);
      }}
    />
  );
}

export function SliderControl({
  field,
  onChange,
  min = 0,
  max = 100,
  unit,
  onOpenConnector,
}: {
  field: VisualEditableField;
  onChange: ApplyFieldValue;
  min?: number;
  max?: number;
  unit?: string;
  onOpenConnector?: OpenVisualPropertyConnector;
}) {
  if (hasActiveConnection([field])) {
    return <FieldConnectionSummary fields={[field]} />;
  }

  const value =
    typeof field.value === "number" && Number.isFinite(field.value)
      ? roundToTwoDecimals(field.value)
      : min;
  const progress =
    max === min
      ? 0
      : Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100));

  return (
    <ConnectorHoverPopover
      onConnect={onOpenConnector ? () => onOpenConnector([field]) : undefined}
    >
      <div className="layer-visual-panel__slider-control">
        <input
          className="layer-visual-panel__range"
          type="range"
          min={min}
          max={max}
          value={value}
          style={{ "--range-progress": `${progress}%` } as CSSProperties}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            onChange(field, Number(event.target.value))
          }
        />
        <span className="layer-visual-panel__slider-value">
          {Math.round(value)}
          {unit}
        </span>
      </div>
    </ConnectorHoverPopover>
  );
}

export function AnchorGridControl({
  value,
  disabled,
  onChange,
}: {
  value?: AnchorPosition;
  disabled?: boolean;
  onChange: (position: AnchorPosition) => void;
}) {
  return (
    <div className="layer-visual-panel__anchor-grid" role="group">
      {ANCHOR_POSITIONS.map((position) => (
        <Tooltip key={position.value} title={position.label}>
          <button
            type="button"
            className={`layer-visual-panel__anchor-point${
              value === position.value
                ? " layer-visual-panel__anchor-point--active"
                : ""
            }`}
            aria-label={`Set anchor ${position.label.toLowerCase()}`}
            aria-pressed={value === position.value}
            disabled={disabled}
            onClick={() => onChange(position.value)}
          >
            <span />
          </button>
        </Tooltip>
      ))}
    </div>
  );
}

export function ColorAndOpacityControl({
  colorField,
  opacityField,
  onChange,
  colorTooltip = "Color",
  opacityTooltip = "Opacity",
  onOpenConnector,
}: {
  colorField?: VisualEditableField;
  opacityField?: VisualEditableField;
  onChange: ApplyFieldValue;
  colorTooltip?: ReactNode;
  opacityTooltip?: ReactNode;
  onOpenConnector?: OpenVisualPropertyConnector;
}) {
  return (
    <div className="layer-visual-panel__color-opacity">
      {colorField && hasActiveConnection([colorField]) ? (
        <div className="layer-visual-panel__color-picker layer-visual-panel__color-picker--connected">
          <FieldConnectionSummary fields={[colorField]} />
        </div>
      ) : colorField ? (
        <ConnectorHoverPopover
          onConnect={
            onOpenConnector ? () => onOpenConnector([colorField]) : undefined
          }
        >
          <Tooltip title={colorTooltip}>
            <div className="layer-visual-panel__color-picker">
              <ColorPicker
                color={toColorHex(colorField.value)}
                clickeable={!colorField.isAnimated}
                size={2}
                onChange={(color) =>
                  onChange(
                    colorField,
                    toColorFieldValue(color.hex, colorField.value)
                  )
                }
              />
            </div>
          </Tooltip>
        </ConnectorHoverPopover>
      ) : null}
      {opacityField && (
        <NumericControl
          field={opacityField}
          onChange={onChange}
          unit="%"
          tooltip={opacityTooltip}
          onOpenConnector={onOpenConnector}
        />
      )}
    </div>
  );
}

export function SelectControl<T extends number | string>({
  field,
  value,
  options,
  disabled,
  onChange,
  onOpenConnector,
}: {
  field?: VisualEditableField;
  value: T;
  options: Array<{ value: T; label: string }>;
  disabled?: boolean;
  onChange: (value: T) => void;
  onOpenConnector?: OpenVisualPropertyConnector;
}) {
  if (field && hasActiveConnection([field])) {
    return <FieldConnectionSummary fields={[field]} />;
  }

  const select = (
    <Select
      value={value}
      options={options}
      disabled={disabled}
      {...layerInspectorPopupProps}
      onChange={onChange}
    />
  );

  if (!field) {
    return select;
  }

  return (
    <ConnectorHoverPopover
      onConnect={onOpenConnector ? () => onOpenConnector([field]) : undefined}
    >
      {select}
    </ConnectorHoverPopover>
  );
}

export function SegmentedButtonControl<T extends number | string>({
  field,
  value,
  options,
  onChange,
  onOpenConnector,
}: {
  field?: VisualEditableField;
  value: T;
  options: Array<{ value: T; label: ReactNode; title: string }>;
  onChange: (value: T) => void;
  onOpenConnector?: OpenVisualPropertyConnector;
}) {
  if (field && hasActiveConnection([field])) {
    return <FieldConnectionSummary fields={[field]} />;
  }

  const control = (
    <div className="layer-visual-panel__segmented-control">
      {options.map((option) => (
        <Tooltip key={String(option.value)} title={option.title}>
          <Button
            size="small"
            type={value === option.value ? "primary" : "default"}
            onClick={() => onChange(option.value)}
            aria-label={option.title}
          >
            {option.label}
          </Button>
        </Tooltip>
      ))}
    </div>
  );

  if (!field) {
    return control;
  }

  return (
    <ConnectorHoverPopover
      onConnect={onOpenConnector ? () => onOpenConnector([field]) : undefined}
    >
      {control}
    </ConnectorHoverPopover>
  );
}

export function ScaleProportionToggle({
  checked,
  onChange,
  showLabel = true,
}: {
  checked: boolean;
  onChange: (nextState: boolean) => void;
  showLabel?: boolean;
}) {
  const toggle = (
    <button
      type="button"
      className={`layer-visual-panel__proportion-toggle${
        checked ? " layer-visual-panel__proportion-toggle--active" : ""
      }${showLabel ? "" : " layer-visual-panel__proportion-toggle--compact"}`}
      aria-label="Lock scale proportions"
      aria-pressed={checked}
      onClick={() => onChange(!checked)}
    >
      {checked ? (
        <LinkOutlined aria-hidden="true" />
      ) : (
        <DisconnectOutlined aria-hidden="true" />
      )}
      {showLabel && <span>{checked ? "Linked scale" : "Split scale"}</span>}
    </button>
  );

  if (showLabel) {
    return toggle;
  }

  return <Tooltip title="Lock proportions">{toggle}</Tooltip>;
}
